import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarDays, CheckCircle2, AlertTriangle, ArrowRight } from "lucide-react";
import { toast } from "../../lib/notify";
import { http, toastApiError } from "../../lib/api";
import { fmtTL, fmtDate } from "../../lib/format";

const DAY_NAMES = ["Paz", "Pzt", "Sal", "Çar", "Per", "Cum", "Cmt"];
const dayLabel = (iso) => {
  const d = new Date(`${iso}T00:00:00`);
  return { name: DAY_NAMES[d.getDay()], date: `${String(d.getDate()).padStart(2, "0")}.${String(d.getMonth() + 1).padStart(2, "0")}` };
};
const itemKey = (it) => `${it.loan_id}-${it.no}`;

function InstItem({ it, canPay, busy, onPay, late = false }) {
  let cls = "border-slate-200 bg-white";
  if (it.paid) cls = "border-emerald-200 bg-emerald-50";
  else if (late) cls = "border-red-300 bg-red-50";
  return (
    <div className={`rounded-md border px-2 py-1.5 text-[11px] space-y-0.5 ${cls}`} data-testid={`inst-cal-item-${itemKey(it)}`}>
      <div className="font-semibold text-slate-800 leading-tight truncate" title={`${it.company ? `${it.company} · ` : ""}${it.bank} ${it.loan_no || ""}`}>{it.bank}{it.loan_no ? <span className="font-normal text-slate-500"> {it.loan_no}</span> : null}</div>
      <div className="text-slate-500">{it.no}/{it.count}. taksit{late ? ` · ${fmtDate(it.due_date)}` : ""}</div>
      <div className="flex items-center justify-between gap-1">
        <span className="font-mono font-semibold">{fmtTL(it.amount)}</span>
        {it.paid
          ? <span className="inline-flex items-center gap-0.5 text-emerald-700 font-semibold" data-testid={`inst-cal-paid-${itemKey(it)}`}><CheckCircle2 className="w-3 h-3" /> ödendi</span>
          : canPay && <button type="button" onClick={() => onPay(it)} disabled={busy === itemKey(it)} data-testid={`inst-cal-pay-${itemKey(it)}`} title="Ödendi işaretle"
              className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-emerald-600 text-emerald-700 font-semibold hover:bg-emerald-100 disabled:opacity-50"><CheckCircle2 className="w-3 h-3" /> Öde</button>}
      </div>
    </div>
  );
}

// Dashboard: önümüzdeki 7 günün kredi taksitleri + vadesi geçmiş ödenmemişler; yönetici buradan ödendi işaretler
export default function InstallmentCalendar({ canPay = false }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);
  const load = useCallback(() => http.get("/bank-loans/upcoming-installments", { params: { days: 7 } }).then((r) => setData(r.data)).catch(() => setData(null)), []);
  useEffect(() => { load(); }, [load]);
  const pay = async (it) => {
    if (!window.confirm(`${it.bank} ${it.loan_no || ""} — ${it.no}. taksit (${fmtTL(it.amount)}) ödendi olarak işaretlensin mi?`)) return;
    setBusy(itemKey(it));
    try { await http.patch(`/bank-loans/${it.loan_id}/installments/${it.no}/pay`); toast.success(`${it.no}. taksit ödendi olarak işaretlendi`); await load(); }
    catch (e) { toastApiError(e); } finally { setBusy(null); }
  };
  if (!data) return null;
  const count = data.days.reduce((a, d) => a + d.items.length, 0);
  return (
    <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="installment-calendar">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
        <CalendarDays className="w-4 h-4 text-amber-600" />
        <div className="text-sm font-semibold text-slate-900">Taksit Ödeme Takvimi</div>
        <div className="text-xs text-slate-500">önümüzdeki 7 gün</div>
        <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 text-[11px] font-semibold" data-testid="inst-cal-count">{count} taksit</span>
        {data.total_due > 0 && <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-[11px] font-bold" data-testid="inst-cal-due">Ödenecek {fmtTL(data.total_due)}</span>}
        {data.late.length > 0 && <span className="px-2 py-0.5 rounded bg-red-100 text-red-700 text-[11px] font-bold inline-flex items-center gap-1" data-testid="inst-cal-late"><AlertTriangle className="w-3 h-3" /> {data.late.length} geciken · {fmtTL(data.late_total)}</span>}
        <Link to="/veri-girisi?bolum=kredi" className="ml-auto inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:underline" data-testid="inst-cal-all">Tüm krediler <ArrowRight className="w-3 h-3" /></Link>
      </div>
      {data.late.length > 0 && (
        <div className="px-4 py-2.5 bg-red-50/60 border-b border-red-100" data-testid="inst-cal-late-list">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-red-700 mb-1.5">Vadesi geçmiş ödenmemiş taksitler</div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-1.5">
            {data.late.slice(0, 12).map((it) => <InstItem key={itemKey(it)} it={it} canPay={canPay} busy={busy} onPay={pay} late />)}
            {data.late.length > 12 && <div className="text-[11px] text-red-700 self-center">+{data.late.length - 12} taksit daha</div>}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 divide-y sm:divide-y-0 lg:divide-x divide-slate-100">
        {data.days.map((d) => {
          const l = dayLabel(d.date);
          const isToday = d.date === data.today;
          return (
            <div key={d.date} className={`p-2 min-h-[88px] ${isToday ? "bg-amber-50/70" : ""}`} data-testid={`inst-cal-day-${d.date}`}>
              <div className="flex items-baseline justify-between mb-1.5">
                <span className={`text-[11px] font-bold ${isToday ? "text-amber-800" : "text-slate-700"}`}>{l.name} <span className="font-normal text-slate-500">{l.date}</span></span>
                {isToday && <span className="text-[9px] uppercase tracking-wider font-bold text-amber-700">Bugün</span>}
              </div>
              <div className="space-y-1">
                {d.items.map((it) => <InstItem key={itemKey(it)} it={it} canPay={canPay} busy={busy} onPay={pay} />)}
                {d.items.length === 0 && <div className="text-[11px] text-slate-300">—</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
